import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { useAuthStore } from '@store'
import { categoryService, postService } from '@services/api'
import apiClient from '@services/api/client'

const AdminVerificationPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  // Récupérer les rôles depuis localStorage
  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')
      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }
  
  // Récupérer le rôle principal depuis localStorage en backup
  const getUserRole = () => {
    if (user?.role) return user.role
    
    try {
      const adminUser = localStorage.getItem('admin_user')
      if (adminUser) {
        const userData = JSON.parse(adminUser)
        if (userData.roles && Array.isArray(userData.roles)) {
          return userData.roles[0]
        }
        if (userData.role) {
          return userData.role
        }
      }
    } catch (e) {
      console.error('Error getting user role:', e)
    }
    return null
  }
  
  const userRoles = getCurrentUserRoles()
  const n = (r) => r.toLowerCase()
  const isSuperAdmin = userRoles.length === 0 || userRoles.some(r => n(r) === 'super admin')
  const isAdmin = userRoles.some(r => n(r) === 'admin')
  const isSuperviseur = userRoles.some(r => n(r) === 'superviseur')
  const isClient = userRoles.some(r => n(r) === 'client')
  
  // Récupérer l'ID du client connecté
  const currentClientId = user?.client_id || user?.id
  
  const userRole = getUserRole()
  
  const [pendingPosts, setPendingPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all') // all, product, video, banner
  const [statusFilter, setStatusFilter] = useState('all') // all, en_attente, publié, archivé
  const [sortBy, setSortBy] = useState('date_desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(6)
  const [selectedPost, setSelectedPost] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [rejectedCount, setRejectedCount] = useState(0)
  const [approvedCount, setApprovedCount] = useState(0)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [postToArchive, setPostToArchive] = useState(null)
  const [postToDelete, setPostToDelete] = useState(null)
  const [postToApprove, setPostToApprove] = useState(null)
  const [postToReject, setPostToReject] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedPosts, setSelectedPosts] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  
  // États pour les modals de modification
  const [showEditArticleModal, setShowEditArticleModal] = useState(false)
  const [showEditVideoModal, setShowEditVideoModal] = useState(false)
  const [showEditBannerModal, setShowEditBannerModal] = useState(false)
  const [postToEdit, setPostToEdit] = useState(null)
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    price: '',
    category_id: '',
    location: '',
    condition: '',
    discount: '',
    delivery: 'Gratuite',
    video_url: '',
    link_url: '',
    images: []
  })
  const [categories, setCategories] = useState([])
  const [previewImages, setPreviewImages] = useState([])


  useEffect(() => {
    loadPendingPosts(true) // Premier chargement avec loading
    loadStats()
    loadCategories()
    
    // Actualiser automatiquement toutes les 30 secondes (silencieusement)
    const interval = setInterval(() => {
      loadPendingPosts(false) // Rafraîchissement silencieux sans loading
      loadStats()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [])

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterType, statusFilter, sortBy])

  const normalizePost = (post) => {
    const statusMap = { 0: 'rejeté', 1: 'en_attente', 2: 'publié', 3: 'archivé' }
    const colorMap  = { 0: 'red', 1: 'yellow', 2: 'green', 3: 'gray' }
    const statusNum = parseInt(post.status ?? 1)
    return {
      ...post,
      id:           post.post_id || post.id,
      pending_id:   post.post_id || post.id,
      title:        post.libele  || post.title  || '',
      post_type:    post.type    != null ? parseInt(post.type) : post.post_type,
      price:        post.prix    || post.price  || '',
      link_url:     post.siteWeb || post.link_url || '',
      status_label: statusMap[statusNum] || 'inconnu',
      status_color: colorMap[statusNum]  || 'gray',
      client_name:  post.client
        ? `${post.client.prenom || ''} ${post.client.nom || ''}`.trim()
        : post.client_name || 'N/A',
      client_phone: post.client?.numero || post.client_phone || '',
      category_name: post.category?.libele_categorie || post.category_name || 'N/A',
      category_id:  post.category?.categorie_id || post.post_categorie_id || '',
    }
  }

  const loadPendingPosts = async (showLoading = true) => {
    if (showLoading) setLoading(true)
    try {
      // exclude_staff: 1 — ne pas afficher les publications sponsorisées créées par le staff
      const result = await postService.getPosts({ exclude_staff: 1 })
      if (result?.success !== false) {
        setPendingPosts((result.data || []).map(normalizePost))
      }
    } catch (error) {
      console.error('❌ Error loading posts:', error)
      if (showLoading) toast.error('Erreur lors du chargement des publications')
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      // Charger les statistiques des rejets et approbations
      const result = await apiClient.get('/dashboard/stats')
      if (result?.success !== false) {
        setRejectedCount(result.rejected_count || 0)
        setApprovedCount(result.approved_count || 0)
      } else {
        // En cas d'échec, mettre des valeurs par défaut
        setRejectedCount(0)
        setApprovedCount(0)
      }
    } catch (error) {
      console.error('Error loading stats:', error)
      // Ne pas afficher d'erreur à l'utilisateur, juste mettre des valeurs par défaut
      setRejectedCount(0)
      setApprovedCount(0)
    }
  }

  const loadCategories = async () => {
    try {
      const result = await categoryService.getCategories()
      if (result?.success !== false) {
        setCategories(result.data || [])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleEditClick = (post) => {
    console.log('===== POST DATA =====')
    console.log('Post complet:', post)
    console.log('Post.images:', post.images)
    console.log('Post.image:', post.image)
    console.log('Post.image_urls:', post.image_urls)
    
    setPostToEdit(post)
    
    // Remplir le formulaire avec les données existantes
    setEditFormData({
      title:       post.title    || post.libele   || '',
      description: post.description || '',
      price:       post.price    || post.prix     || '',
      category_id: post.category_id || post.post_categorie_id || '',
      location:    post.location || '',
      condition:   post.condition || '',
      discount:    post.discount || '',
      delivery:    post.delivery || post.livraison || 'Gratuite',
      video_url:   post.video_url || '',
      link_url:    post.link_url  || post.siteWeb || '',
      images: []
    })
    
    // Charger les images existantes depuis post.fichiers (backend Laravel)
    const BASE = (import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage') + '/'
    const existingImages = []
    if (Array.isArray(post.fichiers) && post.fichiers.length > 0) {
      post.fichiers.forEach(f => {
        if (f.chemin) existingImages.push(BASE + f.chemin)
      })
    }
    setPreviewImages(existingImages)
    
    const postType = parseInt(post.type ?? post.post_type)
    if (postType === 1)      setShowEditVideoModal(true)
    else if (postType === 2) setShowEditArticleModal(true)
    else if (postType === 3) setShowEditBannerModal(true)
    else                     setShowEditArticleModal(true)
  }

  const handleEditFormChange = (e) => {
    const { name, value } = e.target
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleEditImageChange = (e) => {
    const files = Array.from(e.target.files)
    setEditFormData(prev => ({
      ...prev,
      images: files
    }))
    
    // Créer les previews pour les nouvelles images

    const newPreviews = files.map(file => URL.createObjectURL(file))
    
    // Garder les anciennes images et ajouter les nouvelles

    setPreviewImages(prev => [...prev, ...newPreviews])
  }

  const handleRemoveImage = (index) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleEditSubmit = async () => {
    if (!postToEdit) return

    const formData = new FormData()
    formData.append('libele', editFormData.title)
    formData.append('description', editFormData.description)
    if (editFormData.price) formData.append('prix', editFormData.price)
    if (editFormData.category_id) formData.append('post_categorie_id', editFormData.category_id)
    if (editFormData.link_url) formData.append('siteWeb', editFormData.link_url)

    const postType = parseInt(postToEdit.type ?? postToEdit.post_type)
    if (postType === 1) {
      formData.append('video_url', editFormData.video_url)
    }
    
    // Ajouter les nouvelles images
    
    editFormData.images.forEach((image, index) => {
      formData.append(`images[]`, image)
    })

    try {
      const postId = postToEdit.id || postToEdit.post_id
      const result = await postService.updatePost(postId, formData)
      if (result?.success !== false) {
        toast.success('✅ Publication modifiée avec succès!')
        setShowEditArticleModal(false)
        setShowEditVideoModal(false)
        setShowEditBannerModal(false)
        setPostToEdit(null)
        await loadPendingPosts()
      } else {
        toast.error(result.message || 'Erreur lors de la modification')
      }
    } catch (error) {
      console.error('Error updating post:', error)
      toast.error('Erreur lors de la modification')
    }
  }

  const handleApprove = async (post) => {
    setPostToApprove(post)
    setShowApproveModal(true)
  }

  const confirmApprove = async () => {
    if (!postToApprove) return

    try {
      console.log('Post to approve:', postToApprove)
      const postId = postToApprove.id || postToApprove.post_id
      console.log('Using post ID:', postId)
      
      const result = await postService.approvePost(postId)
      if (result?.success !== false) {
        toast.success('✅ Publication approuvée et publiée avec succès!')
        setApprovedCount(prev => prev + 1)
        await loadPendingPosts()
        await loadStats()
        setShowApproveModal(false)
        setPostToApprove(null)
        setShowModal(false)
      } else {
        toast.error(result.message || 'Erreur lors de l\'approbation')
      }
    } catch (error) {
      console.error('Error approving post:', error)
      console.error('Error details:', error.response?.data)
      toast.error(error.response?.data?.message || 'Erreur lors de l\'approbation')
    }
  }

  const handleReject = async (post) => {
    setPostToReject(post)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const confirmReject = async () => {
    if (!postToReject) return

    try {
      console.log('Post to reject:', postToReject)
      const postId = postToReject.id || postToReject.post_id
      console.log('Using post ID for rejection:', postId)
      
      const result = await postService.rejectPost(postId, rejectReason)
      if (result?.success !== false) {
        toast.success('❌ Publication rejetée avec succès!')
        setRejectedCount(prev => prev + 1)
        await loadPendingPosts()
        await loadStats()
        setShowRejectModal(false)
        setPostToReject(null)
        setRejectReason('')
        setShowModal(false)
      } else {
        toast.error(result.message || 'Erreur lors du rejet')
      }
    } catch (error) {
      console.error('Error rejecting post:', error)
      console.error('Error details:', error.response?.data)
      toast.error(error.response?.data?.message || 'Erreur lors du rejet')
    }
  }

  const handleViewDetails = (post) => {
    setSelectedPost(post)
    setShowModal(true)
  }

  const handleArchiveClick = (post) => {
    setPostToArchive(post)
    setShowArchiveModal(true)
  }

  const handleArchive = async () => {
    if (!postToArchive) return

    try {
      console.log('Post to archive:', postToArchive)
      const postId = postToArchive.id || postToArchive.post_id
      const isArchived = postToArchive.status_label === 'archivé' || postToArchive.status === 'archivé'
      console.log('Using post ID for archiving:', postId, 'Currently archived:', isArchived)
      
      const result = await postService.archivePost(postId)
      if (result?.success !== false) {
        toast.success(isArchived ? '📂 Publication désarchivée avec succès!' : '📁 Publication archivée avec succès!')
        await loadPendingPosts(true)
        setShowArchiveModal(false)
        setPostToArchive(null)
      } else {
        toast.error(result.message || 'Erreur lors de l\'archivage')
      }
    } catch (error) {
      console.error('Error archiving post:', error)
      console.error('Error details:', error.response?.data)
      toast.error(error.response?.data?.message || 'Erreur lors de l\'archivage')
    }
  }

  const handleDeleteClick = (post) => {
    setPostToDelete(post)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!postToDelete) return

    try {
      console.log('Post to delete:', postToDelete)
      const postId = postToDelete.id || postToDelete.post_id
      console.log('Using post ID for deletion:', postId)
      
      const result = await postService.deletePost(postId)
      if (result?.success !== false) {
        toast.success('🗑️ Publication supprimée avec succès!')
        await loadPendingPosts()
        setShowDeleteModal(false)
        setPostToDelete(null)
      } else {
        toast.error(result.message || 'Erreur lors de la suppression')
      }
    } catch (error) {
      console.error('Error deleting post:', error)
      console.error('Error details:', error.response?.data)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression')
    }
  }

  const getPostTypeLabel = (type) => {
    switch(type) {
      case 1: return 'Vidéo'
      case 2: return 'Article'
      case 3: return 'Bannière'
      default: return 'Inconnu'
    }
  }

  const getPostTypeIcon = (type) => {
    switch(type) {
      case 1: return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
      case 2: return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
      case 3: return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      )
      default: return null
    }
  }

  const getPostTypeColor = (type) => {
    switch(type) {
      case 1: return 'purple'
      case 2: return 'blue'
      case 3: return 'orange'
      default: return 'gray'
    }
  }

  const filteredPosts = pendingPosts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.client_name?.toLowerCase().includes(searchTerm.toLowerCase())

    let matchesType = true
    if (filterType === 'product') matchesType = post.post_type === 2
    if (filterType === 'video')   matchesType = post.post_type === 1
    if (filterType === 'banner')  matchesType = post.post_type === 3

    let matchesStatus = true
    if (statusFilter !== 'all') matchesStatus = post.status_label === statusFilter

    return matchesSearch && matchesType && matchesStatus
  })
  
  // Appliquer le tri
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      case 'date_asc':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
      case 'name_asc':
        return (a.title || '').localeCompare(b.title || '')
      case 'name_desc':
        return (b.title || '').localeCompare(a.title || '')
      case 'views_desc':
        return (b.views || 0) - (a.views || 0)
      case 'views_asc':
        return (a.views || 0) - (b.views || 0)
      default:
        return 0
    }
  })

  // Pagination
  const totalPages = Math.ceil(sortedPosts.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPosts = sortedPosts.slice(startIndex, endIndex)

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Vérification des Publications</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Approuver ou rejeter les publications en attente
              </p>
            </div>
            <button 
              onClick={loadPendingPosts}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>

        {/* Stats Cards - Première ligne */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-5">
          {/* Total */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setStatusFilter('all')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total Publications</p>
                <p className="text-2xl font-bold text-gray-800">{pendingPosts.length}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {pendingPosts.filter(p => p.status_label === 'en_attente').length} en attente
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>

          {/* Validations (Approuvées) */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setStatusFilter('publié')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Validations</p>
                <p className="text-2xl font-bold text-green-600">
                  {pendingPosts.filter(p => p.status_label === 'publié').length}
                </p>
                <p className="text-xs text-green-500 mt-1">Publications approuvées</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Rejets */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Rejets</p>
                <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
                <p className="text-xs text-red-500 mt-1">Publications rejetées</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Archivées */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setStatusFilter('archivé')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Archivées</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {pendingPosts.filter(p => p.status_label === 'archivé').length}
                </p>
                <p className="text-xs text-yellow-600 mt-1">En archive</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Deuxième ligne : Par type */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {/* Articles */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setFilterType('product')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Articles</p>
                <p className="text-2xl font-bold text-blue-600">
                  {pendingPosts.filter(p => p.post_type === 2).length}
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  {pendingPosts.filter(p => p.post_type === 2 && p.status_label === 'en_attente').length} en attente
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Vidéos */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setFilterType('video')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Vidéos</p>
                <p className="text-2xl font-bold text-purple-600">
                  {pendingPosts.filter(p => p.post_type === 1).length}
                </p>
                <p className="text-xs text-purple-500 mt-1">
                  {pendingPosts.filter(p => p.post_type === 1 && p.status_label === 'en_attente').length} en attente
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Bannières */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
               onClick={() => setFilterType('banner')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Bannières</p>
                <p className="text-2xl font-bold text-orange-600">
                  {pendingPosts.filter(p => p.post_type === 3).length}
                </p>
                <p className="text-xs text-orange-500 mt-1">
                  {pendingPosts.filter(p => p.post_type === 3 && p.status_label === 'en_attente').length} en attente
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Barre de progression et statistiques */}
        {(approvedCount > 0 || rejectedCount > 0) && (
          <div className="bg-gradient-to-r from-green-50 to-red-50 rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Activité de vérification</h3>
                <p className="text-sm text-gray-500">Session actuelle</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
                  <p className="text-xs text-gray-500">Approuvés</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
                  <p className="text-xs text-gray-500">Rejetés</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{approvedCount + rejectedCount}</p>
                  <p className="text-xs text-gray-500">Total traités</p>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="flex h-full rounded-full overflow-hidden">
                <div 
                  className="bg-green-500 transition-all duration-500" 
                  style={{ width: `${approvedCount > 0 ? (approvedCount / (approvedCount + rejectedCount)) * 100 : 0}%` }}
                />
                <div 
                  className="bg-red-500 transition-all duration-500" 
                  style={{ width: `${rejectedCount > 0 ? (rejectedCount / (approvedCount + rejectedCount)) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative md:col-span-2 lg:col-span-1">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par titre, description ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="en_attente">⏳ En attente</option>
              <option value="publié">✓ Publiées</option>
              <option value="archivé">📦 Archivées</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les types</option>
              <option value="product">📦 Articles</option>
              <option value="video">🎥 Vidéos</option>
              <option value="banner">📢 Bannières</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="date_desc">📅 Plus récents</option>
              <option value="date_asc">📅 Plus anciens</option>
              <option value="name_asc">🔤 Titre A-Z</option>
              <option value="name_desc">🔤 Titre Z-A</option>
              <option value="views_desc">👁️ Plus de vues</option>
              <option value="views_asc">👁️ Moins de vues</option>
            </select>
          </div>
        </div>

        {/* Indicateurs de filtres actifs */}
        {(filterType !== 'all' || statusFilter !== 'all' || searchTerm) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-blue-800">Filtres actifs :</span>
                {filterType !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Type: {filterType === 'product' ? 'Articles' : filterType === 'video' ? 'Vidéos' : 'Bannières'}
                    <button onClick={() => setFilterType('all')} className="ml-1 hover:text-blue-900">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Statut: {statusFilter === 'en_attente' ? 'En attente' : statusFilter === 'publié' ? 'Publiées' : 'Archivées'}
                    <button onClick={() => setStatusFilter('all')} className="ml-1 hover:text-blue-900">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Recherche: "{searchTerm}"
                    <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-blue-900">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
              </div>
              <button 
                onClick={() => {
                  setFilterType('all')
                  setStatusFilter('all')
                  setSearchTerm('')
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Réinitialiser tout
              </button>
            </div>
          </div>
        )}

        {/* Table de toutes les publications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Toutes les publications</h2>
              <span className="text-sm text-gray-500">
                {sortedPosts.length} publication{sortedPosts.length > 1 ? 's' : ''} • 
                {' '}{pendingPosts.filter(p => p.status_label === 'en_attente').length} en attente • 
                {' '}{pendingPosts.filter(p => p.status_label === 'publié').length} publiées • 
                {' '}{pendingPosts.filter(p => p.status_label === 'archivé').length} archivées
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        setSelectAll(e.target.checked)
                        if (e.target.checked) {
                          setSelectedPosts(currentPosts.map(p => p.pending_id))
                        } else {
                          setSelectedPosts([])
                        }
                      }}
                      className="w-4 h-4 text-[#001F3F] border-gray-300 rounded focus:ring-2 focus:ring-[#001F3F] cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date création</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentPosts.map((post, index) => {
                  const color = getPostTypeColor(post.post_type)
                  const isArchived = (post.status_label === 'archivé' || post.status === 'archivé')
                  const isPublishedOrArchived = (post.status_label === 'publié' || post.status === 'publié') || (post.status_label === 'archivé' || post.status === 'archivé')
                  
                  // Vérifier si l'utilisateur est le créateur du post
                  const isCreator = post.client_id === currentClientId || post.post_staff_id === currentClientId
                  const canEdit = ['Super Admin', 'Admin', 'Superviseur'].includes(userRole) || isCreator
                  const canArchive = ['Super Admin', 'Admin'].includes(userRole) || isCreator
                  
                  // Debug log
                  console.log('Post:', post.title, 'Status:', post.status, 'Status Label:', post.status_label, 'User Role:', userRole, 'Can Edit:', canEdit, 'Is Creator:', isCreator, 'Is Published/Archived:', isPublishedOrArchived)
                  
                  return (
                    <tr key={post.pending_id} className={`hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${isArchived ? 'bg-gray-100 opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedPosts.includes(post.pending_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPosts([...selectedPosts, post.pending_id])
                            } else {
                              setSelectedPosts(selectedPosts.filter(id => id !== post.pending_id))
                              setSelectAll(false)
                            }
                          }}
                          className="w-4 h-4 text-[#001F3F] border-gray-300 rounded focus:ring-2 focus:ring-[#001F3F] cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 bg-${color}-100 text-${color}-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit`}>
                          {getPostTypeIcon(post.post_type)}
                          {getPostTypeLabel(post.post_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-800 max-w-xs truncate">{post.title}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{post.description}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-700">{post.client_name || 'N/A'}</p>
                        <p className="text-sm text-gray-500 font-mono">{post.client_phone || post.client_telephone || '-'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          {post.category_name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700 font-semibold">
                        {post.price ? `${parseInt(post.price).toLocaleString()} F` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          post.status_color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          post.status_color === 'green' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {post.status_label || post.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {post.created_at ? new Date(post.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                        <div className="text-xs text-gray-400">
                          {post.created_at ? new Date(post.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => handleViewDetails(post)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir détails"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          
                          {/* Bouton Modifier - Visible pour Super Admin, Admin, Superviseur et le créateur */}
                          {canEdit && (
                            <button 
                              onClick={() => handleEditClick(post)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Bouton Archiver - Visible pour le créateur, Admin et Super Admin */}
                          {canArchive && isPublishedOrArchived && (
                            <button 
                              onClick={() => handleArchiveClick(post)}
                              className={`p-2 rounded-lg transition-colors ${
                                isArchived
                                  ? 'text-green-600 hover:bg-green-100' 
                                  : 'text-orange-600 hover:bg-orange-100'
                              }`}
                              title={isArchived ? 'Désarchiver (Rendre visible)' : 'Archiver (Masquer)'}
                            >
                              {isArchived ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                              )}
                            </button>
                          )}
                          
                          {(post.status_label === 'en_attente' || post.status === 'en_attente') && (
                            <>
                              <button 
                                onClick={() => {
                                  console.log('Approving post:', post)
                                  handleApprove(post)
                                }}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Approuver"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              <button 
                                onClick={() => handleReject(post)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Rejeter"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                            </>
                          )}
                          
                          {/* Bouton Supprimer définitif - Uniquement Super Admin */}
                          {isSuperAdmin && isPublishedOrArchived && (
                            <button 
                              onClick={() => handleDeleteClick(post)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer définitivement"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {sortedPosts.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-lg">Aucune publication trouvée</p>
                <p className="text-sm text-gray-400 mt-2">Essayez de modifier vos filtres de recherche</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sortedPosts.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, sortedPosts.length)} sur {sortedPosts.length} publications
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = []
                      const maxVisible = 5 // Nombre maximum de boutons de page visibles
                      
                      if (totalPages <= maxVisible + 2) {
                        // Si peu de pages, afficher toutes
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        // Toujours afficher la première page
                        pages.push(1)
                        
                        let startPage = Math.max(2, currentPage - 1)
                        let endPage = Math.min(totalPages - 1, currentPage + 1)
                        
                        // Ajuster si on est près du début
                        if (currentPage <= 3) {
                          startPage = 2
                          endPage = Math.min(maxVisible, totalPages - 1)
                        }
                        
                        // Ajuster si on est près de la fin
                        if (currentPage >= totalPages - 2) {
                          startPage = Math.max(2, totalPages - maxVisible + 1)
                          endPage = totalPages - 1
                        }
                        
                        // Ellipse au début si nécessaire
                        if (startPage > 2) {
                          pages.push('...')
                        }
                        
                        // Pages du milieu
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(i)
                        }
                        
                        // Ellipse à la fin si nécessaire
                        if (endPage < totalPages - 1) {
                          pages.push('...')
                        }
                        
                        // Toujours afficher la dernière page
                        pages.push(totalPages)
                      }
                      
                      return pages.map((page, index) => {
                        if (page === '...') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400">
                              ...
                            </span>
                          )
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-[#D4AF37] text-white font-semibold'
                                : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      })
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Détails */}
      {showModal && selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header */}
            <div className="px-8 py-6 bg-blue-50/50 border-b-2 border-blue-100 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  {getPostTypeIcon(selectedPost.post_type)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-600">Détails de la publication</h2>
                  <p className="text-blue-500 text-sm mt-0.5">{getPostTypeLabel(selectedPost.post_type)}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-blue-50 rounded-xl transition-all duration-200"
              >
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-8 space-y-6 overflow-y-auto flex-1 bg-gray-50">
              {/* Images */}
              {selectedPost.uploaded_files && selectedPost.uploaded_files.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-bold text-gray-800">Images de la publication</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedPost.uploaded_files.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={`http://localhost/ligdi/public/${img}`} 
                          alt={`Image ${idx + 1}`}
                          className="w-full h-40 object-cover rounded-xl border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23ddd" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23999" dy=".3em">Image indisponible</text></svg>';
                          }}
                        />
                        <div className="absolute top-2 left-2 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                          <span className="text-white text-sm font-bold">{idx + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Titre et Description */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Contenu de la publication</h3>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{selectedPost.title}</h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedPost.description}</p>
                </div>
              </div>

              {/* Informations */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Informations détaillées</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200">
                    <label className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Client</label>
                    <p className="text-base font-bold text-gray-900 mt-2">{selectedPost.client_name || 'N/A'}</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200">
                    <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Catégorie</label>
                    <p className="text-base font-bold text-gray-900 mt-2">{selectedPost.category_name || 'N/A'}</p>
                  </div>

                  {selectedPost.price && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 border border-green-200">
                      <label className="text-xs font-semibold text-green-600 uppercase tracking-wide">Prix</label>
                      <p className="text-2xl font-bold text-green-700 mt-2">
                        {parseInt(selectedPost.price).toLocaleString()} <span className="text-sm">FCFA</span>
                      </p>
                    </div>
                  )}

                  {selectedPost.location && (
                    <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg p-4 border border-red-200">
                      <label className="text-xs font-semibold text-red-600 uppercase tracking-wide">Localisation</label>
                      <p className="text-base font-semibold text-gray-900 mt-2 flex items-center gap-2">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {selectedPost.location}
                      </p>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-lg p-4 border border-indigo-200">
                    <label className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Date de création</label>
                    <p className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(selectedPost.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                    <label className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Date d'expiration</label>
                    <p className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {new Date(selectedPost.expires_at).toLocaleString('fr-FR')}
                    </p>
                  </div>

                  {selectedPost.duration && (
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 rounded-lg p-4 border border-cyan-200">
                      <label className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">Durée d'affichage</label>
                      <p className="text-base font-bold text-gray-900 mt-2">{selectedPost.duration} jour(s)</p>
                    </div>
                  )}

                  {selectedPost.link && (
                    <div className="col-span-2 bg-gradient-to-br from-pink-50 to-pink-100/50 rounded-lg p-4 border border-pink-200">
                      <label className="text-xs font-semibold text-pink-600 uppercase tracking-wide mb-2 block">Lien</label>
                      <a href={selectedPost.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium hover:underline flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {selectedPost.link}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  if (selectedPost.client_id) {
                    // Naviguer vers la page messages avec les infos de l'article
                    navigate('/admin/messages', {
                      state: {
                        clientId: selectedPost.client_id,
                        clientName: selectedPost.client_name,
                        postDetails: {
                          id: selectedPost.id,
                          title: selectedPost.title,
                          description: selectedPost.description,
                          type: getPostTypeLabel(selectedPost.post_type),
                          price: selectedPost.price,
                          location: selectedPost.location,
                          category: selectedPost.category_name,
                          images: selectedPost.uploaded_files
                        }
                      }
                    })
                  } else {
                    toast.error('Impossible de contacter ce client')
                  }
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-semibold flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Contacter le client
              </button>
              <button
                onClick={() => handleReject(selectedPost)}
                className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-semibold flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Rejeter
              </button>
              <button
                onClick={() => handleApprove(selectedPost)}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-semibold flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Approuver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Archiver/Désarchiver */}
      {showArchiveModal && postToArchive && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 ${postToArchive.status === 'archivé' ? 'bg-green-100' : 'bg-orange-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <svg className={`w-7 h-7 ${postToArchive.status === 'archivé' ? 'text-green-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">
                    {postToArchive.status === 'archivé' ? 'Désarchiver' : 'Archiver'} la publication
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {postToArchive.status === 'archivé' 
                      ? 'Cette publication sera à nouveau visible'
                      : 'Cette publication ne sera plus visible publiquement'
                    }
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Publication :</p>
                <p className="text-base font-semibold text-gray-900">{postToArchive.title}</p>
              </div>

              <p className="text-gray-600 mb-6">
                {postToArchive.status === 'archivé'
                  ? 'Êtes-vous sûr de vouloir désarchiver cette publication ? Elle redeviendra visible pour tous les utilisateurs.'
                  : 'Êtes-vous sûr de vouloir archiver cette publication ? Elle ne sera plus visible publiquement mais restera accessible dans les archives.'
                }
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowArchiveModal(false)
                    setPostToArchive(null)
                  }}
                  className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-semibold"
                >
                  Annuler
                </button>
                <button
                  onClick={handleArchive}
                  className={`px-8 py-3 ${postToArchive.status === 'archivé' ? 'bg-gradient-to-r from-green-600 to-green-700' : 'bg-gradient-to-r from-orange-600 to-orange-700'} text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center gap-2`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {postToArchive.status === 'archivé' ? 'Désarchiver' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Approuver */}
      {showApproveModal && postToApprove && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all overflow-hidden">
            {/* Header avec fond transparent vert */}
            <div className="bg-green-50/50 px-6 py-4 border-b border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-700">Approuver la publication</h3>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-5">
              <p className="text-gray-700 font-medium leading-relaxed text-center">
                Êtes-vous sûr de vouloir approuver cette publication ?
              </p>
            </div>

            {/* Footer avec boutons */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-center">
              <button
                onClick={() => {
                  setShowApproveModal(false)
                  setPostToApprove(null)
                }}
                className="px-8 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium shadow-sm"
              >
                Annuler
              </button>
              <button
                onClick={confirmApprove}
                className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all font-medium shadow-lg hover:shadow-xl"
              >
                Oui, approuver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rejeter */}
      {showRejectModal && postToReject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all overflow-hidden">
            {/* Header avec fond transparent rouge */}
            <div className="bg-red-50/50 px-6 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-700">Rejeter la publication</h3>
                  <p className="text-sm text-red-600">Indiquez la raison du rejet</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison du rejet (optionnelle) :
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Indiquez la raison du rejet..."
                rows="4"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all outline-none resize-none"
                autoFocus
              />
            </div>

            {/* Footer avec boutons */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-center">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setPostToReject(null)
                  setRejectReason('')
                }}
                className="px-8 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium shadow-sm"
              >
                Annuler
              </button>
              <button
                onClick={confirmReject}
                className="px-8 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all font-medium shadow-lg hover:shadow-xl"
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Supprimer */}
      {showDeleteModal && postToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all overflow-hidden">
            {/* Header avec fond transparent rouge */}
            <div className="bg-red-50/50 px-6 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-700">Supprimer la publication</h3>
                  <p className="text-sm text-red-600">Cette action est irréversible</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-5">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-red-800 mb-2">Publication :</p>
                <p className="text-base font-semibold text-red-900">{postToDelete.title}</p>
                <p className="text-xs text-red-700 mt-2">Client : {postToDelete.client_name || 'N/A'}</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-yellow-900 mb-1">⚠️ Attention</p>
                    <p className="text-sm text-yellow-800">
                      Cette publication sera définitivement supprimée de la base de données. 
                      Toutes les images et fichiers associés seront également supprimés.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 font-medium leading-relaxed">
                Êtes-vous absolument sûr de vouloir supprimer cette publication ?
              </p>
            </div>

            {/* Footer avec boutons */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setPostToDelete(null)
                }}
                className="px-8 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium shadow-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-8 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all font-medium shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Article */}
      {showEditArticleModal && postToEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 border-b sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white">Modifier l'Article</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditFormChange}
                  placeholder="Veuillez écrire un titre clair pour votre article"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie<span className="text-red-500">*</span>
                </label>
                <select
                  name="category_id"
                  value={editFormData.category_id}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat.categorie_id} value={cat.categorie_id}>{cat.libele_categorie}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Situation géographique<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditFormChange}
                  placeholder="Votre emplacement"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description<span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditFormChange}
                  rows={5}
                  placeholder="Décrivez votre article"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  État<span className="text-red-500">*</span>
                </label>
                <select
                  name="condition"
                  value={editFormData.condition}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez l'état</option>
                  <option value="neuf">Neuf</option>
                  <option value="scelle">Scellé</option>
                  <option value="reconditionne">Reconditionné</option>
                  <option value="quasi_neuf">Quasi neuf</option>
                  <option value="excellent">Excellent</option>
                  <option value="bon">Bon état</option>
                  <option value="moyen">État moyen</option>
                  <option value="usage">Usagé</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={editFormData.price}
                    onChange={handleEditFormChange}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appliquer une réduction<span className="text-red-500">*</span>
                  </label>
                  <select
                    name="discount"
                    value={editFormData.discount}
                    onChange={handleEditFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choisissez un champ</option>
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="10">10%</option>
                    <option value="15">15%</option>
                    <option value="20">20%</option>
                    <option value="25">25%</option>
                    <option value="30">30%</option>
                    <option value="50">50%</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Livraison<span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="delivery"
                      value="Gratuite"
                      checked={editFormData.delivery === 'Gratuite'}
                      onChange={handleEditFormChange}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700">Gratuite</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="delivery"
                      value="Payante"
                      checked={editFormData.delivery === 'Payante'}
                      onChange={handleEditFormChange}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700">Payante</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajouter une photo
                </label>
                <p className="text-sm text-gray-500 mb-2">Choisissez le nombre d'images :</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleEditImageChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Cliquez sur une image pour la définir comme image principale (affichée en premier).
                </p>
                {previewImages.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mt-3 mb-2">
                      Images actuelles ({previewImages.length})
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {previewImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={img} 
                            alt={`Image ${idx + 1}`} 
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all" 
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            title="Supprimer cette image"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          {idx === 0 && (
                            <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                              Principale
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end sticky bottom-0">
              <button
                onClick={() => {
                  setShowEditArticleModal(false)
                  setPostToEdit(null)
                }}
                className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-lg font-medium transition-all"
              >
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Vidéo */}
      {showEditVideoModal && postToEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 border-b sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white">Modifier la Vidéo</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditFormChange}
                  placeholder="Titre de votre vidéo"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie<span className="text-red-500">*</span>
                </label>
                <select
                  name="category_id"
                  value={editFormData.category_id}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat.categorie_id} value={cat.categorie_id}>{cat.libele_categorie}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Situation géographique<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditFormChange}
                  placeholder="Votre emplacement"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description<span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditFormChange}
                  rows={5}
                  placeholder="Décrivez votre vidéo"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix (FCFA)
                </label>
                <input
                  type="number"
                  name="price"
                  value={editFormData.price}
                  onChange={handleEditFormChange}
                  placeholder="Ex: 50000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  État
                </label>
                <select
                  name="condition"
                  value={editFormData.condition}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez l'état</option>
                  <option value="neuf">Neuf</option>
                  <option value="scelle">Scellé</option>
                  <option value="reconditionne">Reconditionné</option>
                  <option value="quasi_neuf">Quasi neuf</option>
                  <option value="excellent">Excellent</option>
                  <option value="bon">Bon état</option>
                  <option value="moyen">État moyen</option>
                  <option value="usage">Usagé</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Réduction (%)
                </label>
                <input
                  type="number"
                  name="discount"
                  value={editFormData.discount}
                  onChange={handleEditFormChange}
                  placeholder="Ex: 10"
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-800 mb-3">Fichier vidéo</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier vidéo<span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Formats acceptés: MP4, AVI, MOV, WebM. Taille maximale: 500 MB</p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-600">Cliquez pour télécharger une vidéo</p>
                      <p className="text-xs text-gray-400">ou glissez-déposez votre fichier ici</p>
                    </div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          setEditFormData(prev => ({ ...prev, video_file: file }))
                        }
                      }}
                      className="hidden"
                      id="video-upload-edit"
                    />
                  </div>
                  <label htmlFor="video-upload-edit" className="cursor-pointer" />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Miniature (optionnel)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Image de couverture pour votre vidéo</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditImageChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                  {previewImages.length > 0 && (
                    <div className="mt-3">
                      <img src={previewImages[0]} alt="Miniature" className="w-full h-48 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end sticky bottom-0">
              <button
                onClick={() => {
                  setShowEditVideoModal(false)
                  setPostToEdit(null)
                }}
                className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:shadow-lg font-medium transition-all"
              >
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Bannière */}
      {showEditBannerModal && postToEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 border-b sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white">Modifier la Bannière Publicitaire</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditFormChange}
                  placeholder="Titre de votre bannière"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie<span className="text-red-500">*</span>
                </label>
                <select
                  name="category_id"
                  value={editFormData.category_id}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat.categorie_id} value={cat.categorie_id}>{cat.libele_categorie}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lien de destination<span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="link_url"
                  value={editFormData.link_url}
                  onChange={handleEditFormChange}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description<span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditFormChange}
                  rows={4}
                  placeholder="Décrivez votre bannière publicitaire"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image de la bannière<span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Dimensions recommandées: 1200x300 pixels. Formats acceptés: JPG, PNG, WebP</p>
                <label htmlFor="banner-upload-edit" className="block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition-colors cursor-pointer">
                  <div className="flex flex-col items-center gap-2 pointer-events-none">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-600">Cliquez pour télécharger une image</p>
                    <p className="text-xs text-gray-400">ou glissez-déposez votre fichier ici</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditImageChange}
                    className="hidden"
                    id="banner-upload-edit"
                  />
                </label>
                {previewImages.length > 0 && (
                  <div className="mt-3">
                    <img src={previewImages[0]} alt="Bannière" className="w-full h-48 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end sticky bottom-0">
              <button
                onClick={() => {
                  setShowEditBannerModal(false)
                  setPostToEdit(null)
                }}
                className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:shadow-lg font-medium transition-all"
              >
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminVerificationPage

